import { useCallback } from 'react';
import { useInstantSearch } from 'react-instantsearch';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import { Option } from '@sorare/core/src/components/search/Option';
import { useNextSo5FixtureTeamSlugs } from '@sorare/core/src/hooks/config/useNextSo5FixtureTeamSlugs';
import { useVirtualToggle } from '@sorare/core/src/hooks/useVirtualToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import {
  FilterWidget,
  VIRTUAL_TOGGLE_FILTERS,
} from '@sorare/core/src/lib/filters';

const FILTERS = {
  [So5FixtureEvent.CLASSIC]: VIRTUAL_TOGGLE_FILTERS.gameweekFilter,
  [So5FixtureEvent.DAILY]: VIRTUAL_TOGGLE_FILTERS.dailyFilter,
};

type Props = {
  visible: boolean;
  eventType: So5FixtureEvent.CLASSIC | So5FixtureEvent.DAILY;
};

const FixtureFilter = ({ visible, eventType }: Props) => {
  const FILTER = FILTERS[eventType];

  const teamsPlayingNextFixture = useNextSo5FixtureTeamSlugs(eventType);
  const setVirtualToggle = useVirtualToggleManager();
  const { currentRefinement } = useVirtualToggle<string>({
    name: FILTER.name,
  });

  const track = useEvents();
  const onChange = useCallback(
    (gw: string) => {
      const isActive = currentRefinement === gw;
      const newValue = isActive ? undefined : gw;
      setVirtualToggle({
        [FILTER.name]: newValue,
      });

      if (!isActive) {
        track('Use Market Filter', {
          filterName: FILTER.trackingName,
          filterValue: gw,
        });
      }
    },
    [
      FILTER.name,
      FILTER.trackingName,
      currentRefinement,
      setVirtualToggle,
      track,
    ]
  );

  if (!visible) return <FilterSection />;

  return (
    <FilterSection>
      {teamsPlayingNextFixture.map(({ gameWeek, longDisplayName, slug }) => (
        <Option
          key={gameWeek}
          active={currentRefinement === slug}
          onClick={() => onChange(slug)}
          variant="radio"
        >
          {longDisplayName}
        </Option>
      ))}
    </FilterSection>
  );
};

type TitleProps = {
  eventType: So5FixtureEvent.CLASSIC | So5FixtureEvent.DAILY;
  short?: boolean;
};

const Title = ({ eventType, short }: TitleProps) => {
  const FILTER = FILTERS[eventType];
  const { indexUiState } = useInstantSearch<ExtendedUIState>();
  const selected = !!indexUiState?.virtualToggle?.[FILTER.name];
  return (
    <FilterTitle
      name={
        (short && 'shortTitle' in FILTER && FILTER?.shortTitle) || FILTER.title!
      }
      selected={selected}
    />
  );
};

export const GameweekFilter: FilterWidget = {
  key: VIRTUAL_TOGGLE_FILTERS.gameweekFilter.name,
  type: 'virtualToggle',
  filter: VIRTUAL_TOGGLE_FILTERS.gameweekFilter,
  Component: props => (
    <FixtureFilter {...props} eventType={So5FixtureEvent.CLASSIC} />
  ),
  title: <Title eventType={So5FixtureEvent.CLASSIC} />,
};

export const DailyFilter: FilterWidget = {
  key: VIRTUAL_TOGGLE_FILTERS.dailyFilter.name,
  type: 'virtualToggle',
  filter: VIRTUAL_TOGGLE_FILTERS.dailyFilter,
  Component: props => (
    <FixtureFilter {...props} eventType={So5FixtureEvent.DAILY} />
  ),
  title: <Title eventType={So5FixtureEvent.DAILY} />,
  shortTitle: <Title eventType={So5FixtureEvent.DAILY} short />,
};
